import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../../components/cancelButton/CancelButton";
import { useProducts } from "../useProducts";

interface DialogProps {
  readonly open: boolean;
  readonly productId: string;
  readonly providerId: string;
  readonly optionName: string;
  onSelectOption: (value: { productId: string; optionId: string }) => Promise<void>;
  onClose: () => void;
}

const SelectOptionToLinkDialog: FC<DialogProps> = ({
  open,
  productId,
  providerId,
  optionName,
  onClose,
  onSelectOption,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const { products, isLoading } = useProducts();
  const matchingOptions = products
    .filter((p) => p.provider.id === providerId)
    .filter((p) => p.id !== productId)
    .flatMap((p) =>
      p.options
        .filter((o) => o.name === optionName)
        .map((o) => ({ optionId: o.id, optionName: o.name, productId: p.id, productName: p.name })),
    );
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Wybierz opcję</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Typography variant="body2">Wybierz opcję której cennika chesz użyc</Typography>
          {isLoading && <LinearProgress />}
          <Select value={selectedOption} fullWidth onChange={(e) => setSelectedOption(e.target.value)}>
            {matchingOptions.map((o) => (
              <MenuItem key={o.optionId} value={`${o.productId}:${o.optionId}`}>
                <Typography>
                  {o.productName} <b>{o.optionName}</b>
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <AsyncLoadingButton
          disabled={!selectedOption}
          onClick={() => {
            const [selectedProductId, selectedOptionId] = selectedOption.split(":");
            return onSelectOption({ productId: selectedProductId, optionId: selectedOptionId });
          }}
        >
          {" "}
          Użyj wybranego cennika
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface Props {
  readonly productId: string;
  readonly providerId: string;
  readonly optionName: string;
  onSelectOption: (value: { productId: string; optionId: string }) => Promise<void>;
}

const LinkOptionPrices: FC<Props> = ({ productId, providerId, optionName, onSelectOption }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" color="secondary" onClick={() => setDialogOpen(true)}>
        Przypisz cennik z innej opcji
      </Button>
      <SelectOptionToLinkDialog
        open={dialogOpen}
        productId={productId}
        providerId={providerId}
        optionName={optionName}
        onClose={() => setDialogOpen(false)}
        onSelectOption={async (v) => {
          await onSelectOption(v);
          setDialogOpen(false);
        }}
      />
    </>
  );
};

export default LinkOptionPrices;
